import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { isDocEntityType, resolveActor } from '../utils/notificationInbox'
import type { NotificationDocument } from '@/lib/db/rxdb/schemas/notification'
import type { MemberDocument } from '@/lib/db/rxdb/schemas/member'
import type {
  PageDocument,
  ProjectDocument,
  StatusDocument,
  TaskDocument,
} from '@/lib/db/rxdb/hooks'
import { usePages, useProjects, useStatuses, useTasks } from '@/lib/db/rxdb/hooks'
import { useMembers } from '@/lib/hooks/useWorkspaceUsers'
import { resolveTaskIdentifier } from '@/routes/features/tasks/utils/taskIdentifier'
import useLoginStore from '@/store/LoginStore'

export interface NotificationSubject {
  kind: 'task' | 'doc' | 'cycle' | 'none'
  /** Short reference shown in mono, e.g. "ZYR-4". Empty when unknown. */
  identifier: string
  /** Task name / doc title, falling back to the notification title. */
  title: string
  task: TaskDocument | null
  page: PageDocument | null
  /** The top-level space the task or doc lives in. */
  space: ProjectDocument | null
  /** The project (may equal the space) the task or doc lives in. */
  project: ProjectDocument | null
}

/**
 * Client-side joins the inbox needs: which task/doc a notification is about,
 * who the actor is, and the workspace's statuses. One subscription per
 * collection for the whole page rather than per row.
 */
export function useNotificationLookups() {
  const { t } = useTranslation('notifications')
  const currentUserId = useLoginStore((s) => s.userId)
  const { tasks } = useTasks({ isDeleted: false })
  const { projects } = useProjects({ isDeleted: false })
  const { pages } = usePages({ isDeleted: false })
  const { statuses } = useStatuses({ isDeleted: false })
  const { users: members } = useMembers()

  const taskById = useMemo(() => new Map(tasks.map((x) => [x.id, x])), [tasks])
  const projectById = useMemo(
    () => new Map<string, ProjectDocument>(projects.map((x) => [x.id, x])),
    [projects],
  )
  const pageById = useMemo(() => new Map(pages.map((x) => [x.id, x])), [pages])
  const statusById = useMemo(
    () => new Map<string, StatusDocument>(statuses.map((x) => [x.id, x])),
    [statuses],
  )
  const memberByUserId = useMemo(
    () => new Map<string, MemberDocument>(members.map((m) => [m.userId, m])),
    [members],
  )
  const nameByUserId = useMemo(() => {
    const map = new Map<string, string>()
    for (const m of members) {
      const name = m.name || m.email
      if (name) map.set(m.userId, name)
    }
    return map
  }, [members])

  const spaceOf = useCallback(
    (projectId: string | null | undefined) => {
      const project = projectId ? (projectById.get(projectId) ?? null) : null
      if (!project) return { project: null, space: null }
      const space = project.parentProjectId
        ? (projectById.get(project.parentProjectId) ?? project)
        : project
      return { project, space }
    },
    [projectById],
  )

  const resolveSubject = useCallback(
    (notification: NotificationDocument): NotificationSubject => {
      const { entityType, entityId } = notification
      if (entityId && isDocEntityType(entityType)) {
        const page = pageById.get(entityId) ?? null
        const { project, space } = spaceOf(page?.projectId)
        return {
          kind: 'doc',
          identifier: t('subject.doc'),
          title: page?.title || notification.title,
          task: null,
          page,
          space,
          project,
        }
      }
      if (entityType === 'cycle') {
        return {
          kind: 'cycle',
          identifier: t('subject.cycle'),
          title: notification.message || notification.title,
          task: null,
          page: null,
          space: null,
          project: null,
        }
      }
      const task = entityId ? (taskById.get(entityId) ?? null) : null
      if (task) {
        const { project, space } = spaceOf(task.projectId)
        return {
          kind: 'task',
          identifier: resolveTaskIdentifier(task, project?.projectKey ?? null),
          title: task.name,
          task,
          page: null,
          space,
          project,
        }
      }
      return {
        kind: 'none',
        identifier: '',
        title: notification.title,
        task: null,
        page: null,
        space: null,
        project: null,
      }
    },
    [pageById, spaceOf, t, taskById],
  )

  const actorLabel = useCallback(
    (userId: string | null | undefined) => {
      const actor = resolveActor(userId, currentUserId, nameByUserId)
      if (actor.kind === 'you') return t('actor.you')
      if (actor.kind === 'member') return actor.name
      return t('actor.unknown')
    },
    [currentUserId, nameByUserId, t],
  )

  return {
    currentUserId,
    memberByUserId,
    statusById,
    resolveSubject,
    actorLabel,
  }
}

export type NotificationLookups = ReturnType<typeof useNotificationLookups>
